import { IncDocument } from "@core/domain/models/inc-document.model";
import { DeleteDocumentUseCase } from "@core/domain/usecases/delete-document.usecase";
import { DownloadDocumentUseCase } from "@core/domain/usecases/download-document.usecase";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { CCAA } from "@entity/domain/models/ccaa.model";
import { IVAType } from "@entity/domain/models/cost/iva-type.model";
import { GetAllIvaTypesUseCase } from "@entity/domain/usecases/cost/get-all-iva-types.usecase";
import { GetAllCCAAUseCase } from "@entity/domain/usecases/get-all-ccaa.usecase";
import { TransportType } from "@project/domain/models/expenses/transport-type.model";
import { GetAllTransportTypesUseCase } from "@project/domain/usecases/expenses/get-all-transport-types.usecase";
import { Map } from "immutable";
import { inject, injectable } from "inversify";
import { flow, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class ExpenseTravelFormViewModel extends BaseViewModel {
    @observable
    initialLoading: boolean = true;

    @observable
    transportTypes: TransportType[] = [];

    @observable
    ccaaTypes: CCAA[] = [];

    @observable
    ivaTypes: IVAType[] = [];

    @observable
    documents: Map<number, IncDocument> = Map([]);

    constructor(
        @inject(GetAllTransportTypesUseCase)
        private readonly getAllTransportTypesUseCase: GetAllTransportTypesUseCase,
        @inject(GetAllIvaTypesUseCase)
        private readonly getAllIvaTypesUseCase: GetAllIvaTypesUseCase,
        @inject(GetAllCCAAUseCase)
        private readonly getAllCcaaUseCase: GetAllCCAAUseCase,
        @inject(DeleteDocumentUseCase)
        private readonly deleteDocumentUseCase: DeleteDocumentUseCase,
        @inject(DownloadDocumentUseCase)
        private readonly downloadDocumentUseCase: DownloadDocumentUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await super.didMount();

        this.initViewData();
    }

    @flow
    async *initViewData(): AsyncGenerator<void> {
        await Promise.all([
            this.getAllTransportTypes(),
            this.getAllIvaTypes(),
            this.getAllCcaaTypes(),
        ]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @flow
    async *getAllTransportTypes(): AsyncGenerator<void> {
        const transportTypes = await this.getAllTransportTypesUseCase.execute();

        runInAction(() => {
            this.transportTypes = transportTypes;
        });
    }

    async getAllCcaaTypes(): Promise<void> {
        const ccaaTypes = await this.getAllCcaaUseCase.execute();

        if (ccaaTypes) {
            runInAction(() => {
                this.ccaaTypes = ccaaTypes;
            });
        }
    }

    async getAllIvaTypes(): Promise<void> {
        const ivaTypes = await this.getAllIvaTypesUseCase.execute();

        runInAction(() => {
            this.ivaTypes = ivaTypes;
        });
    }

    async deleteDocument(documentId: number): Promise<void> {
        LoadLayoutStore.start();

        const deleted = await this.deleteDocumentUseCase.execute(documentId);

        if (deleted) {
            ToastManagerStore.success();
        }

        LoadLayoutStore.finish();
    }

    async downloadDocument(documentUrl: string): Promise<void> {
        LoadLayoutStore.start();

        await this.downloadDocumentUseCase.execute(documentUrl);

        LoadLayoutStore.finish();
    }
}
